<script>
/**
 * Buyer / supplier / consignee card. Single role per instance; lay out side
 * by side with a `.print-party-row` flex/grid container in the parent.
 *
 * Props:
 *   role:    "buyer" | "supplier" | "consignee" | "ship-to" | string
 *   name, address, gstin, pan, phone, email, stateName: each optional
 */
export default {
  name: "PrintPartyBlock",
  props: {
    role: { type: String, required: true },
    name: { type: String, default: "" },
    address: { type: String, default: "" },
    gstin: { type: String, default: "" },
    pan: { type: String, default: "" },
    phone: { type: String, default: "" },
    email: { type: String, default: "" },
    stateName: { type: String, default: "" },
    placeOfSupply: { type: String, default: "" },
  },
  computed: {
    roleLabel() {
      const map = {
        buyer: "Bill To",
        supplier: "Bill From",
        consignee: "Consignee",
        "ship-to": "Ship To",
      };
      return map[this.role] || this.role;
    },
  },
};
</script>

<template>
  <div class="print-party">
    <div class="print-party-role">{{ roleLabel }}</div>
    <div class="print-party-name">{{ name || "—" }}</div>
    <div v-if="address" class="print-party-address">{{ address }}</div>
    <div v-if="stateName" class="print-party-line">
      <span class="print-party-label">State:</span> {{ stateName }}
    </div>
    <div v-if="placeOfSupply" class="print-party-line">
      <span class="print-party-label">Place of Supply:</span> {{ placeOfSupply }}
    </div>
    <div v-if="gstin" class="print-party-line">
      <span class="print-party-label">GSTIN:</span>
      <span class="print-mono">{{ gstin }}</span>
    </div>
    <div v-if="pan" class="print-party-line">
      <span class="print-party-label">PAN:</span>
      <span class="print-mono">{{ pan }}</span>
    </div>
    <div v-if="phone || email" class="print-party-line">
      <template v-if="phone">{{ phone }}</template>
      <template v-if="phone && email">  •  </template>
      <template v-if="email">{{ email }}</template>
    </div>
  </div>
</template>

<style scoped>
.print-party {
  border: 0.5pt solid var(--print-border);
  padding: 1.5mm 2mm;
  font-size: var(--print-fs-sm);
  line-height: 1.4;
  page-break-inside: avoid;
  break-inside: avoid;
}

.print-party-role {
  color: var(--print-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: var(--print-fs-xs);
  font-weight: 600;
  margin-bottom: 0.5mm;
}

.print-party-name {
  font-weight: 700;
  font-size: var(--print-fs-base);
  margin-bottom: 0.5mm;
}

.print-party-address {
  white-space: pre-wrap;
  margin-bottom: 0.5mm;
}

.print-party-line {
  font-size: var(--print-fs-xs);
}

.print-party-label {
  color: var(--print-muted);
  font-weight: 500;
  margin-right: 0.5mm;
}
</style>
